import type {
  ContextDockContent,
  ContextDockSource,
} from '@affine/core/modules/streamlain/copilot/context-dock';
import { createIdentifier } from '@blocksuite/affine/global/di';
import type { EdgelessDock } from '@blocksuite/affine/widgets/edgeless-toolbar';
import {
  BehaviorSubject,
  combineLatest,
  map,
  type Observable,
  of,
  switchMap,
} from 'rxjs';

import {
  edgelessDockContent$,
  EMPTY_EDGELESS_TOOLS,
} from './edgeless-dock-source';

/** Takes the files the user picks beside the island into the document. */
export type DocumentCapture = (files: readonly File[]) => void;

/**
 * A document's dock in its view: the canvas's tools (edgeless) and the document's capture of
 * photos and files, as one context-dock source. The document provides it while any part is published.
 */
export class DocumentContextDock {
  private readonly _tools$ = new BehaviorSubject<EdgelessDock | null>(null);

  private readonly _capture$ = new BehaviorSubject<DocumentCapture | null>(
    null
  );

  private _row: HTMLElement | null = null;

  private _releaseRow: (() => void) | null = null;

  private _room = 0;

  private _release: (() => void) | null = null;

  constructor(
    private readonly _provide: (source: ContextDockSource) => () => void,
    private readonly _presentationLabel: () => string
  ) {}

  private readonly _content$: Observable<ContextDockContent> = combineLatest([
    this._tools$.pipe(
      switchMap(dock =>
        dock
          ? edgelessDockContent$(dock, this._presentationLabel)
          : of(EMPTY_EDGELESS_TOOLS)
      )
    ),
    this._capture$,
  ]).pipe(
    map(
      ([tools, capture]): ContextDockContent => ({
        ...tools,
        capture,
      })
    )
  );

  readonly source: ContextDockSource = {
    content$: this._content$,
    attach: row => {
      this._row = row;
      this._attachRow();
      return () => {
        if (this._row !== row) return;
        this._row = null;
        this._attachRow();
      };
    },
    setRoom: width => {
      this._room = width;
      this._tools$.value?.setRoom(width);
    },
  };

  publishTools(dock: EdgelessDock) {
    this._tools$.next(dock);
    dock.setRoom(this._room);
    this._attachRow();
    this._sync();
    return () => {
      if (this._tools$.value !== dock) return;
      this._tools$.next(null);
      this._attachRow();
      this._sync();
    };
  }

  publishCapture(capture: DocumentCapture) {
    this._capture$.next(capture);
    this._sync();
    return () => {
      if (this._capture$.value !== capture) return;
      this._capture$.next(null);
      this._sync();
    };
  }

  /** The canvas keeps the dock row as its overlay and its tools' menus above it. */
  private _attachRow() {
    this._releaseRow?.();
    this._releaseRow = null;
    const dock = this._tools$.value;
    if (dock && this._row) this._releaseRow = dock.attach(this._row);
  }

  private _sync() {
    const published = !!(this._tools$.value || this._capture$.value);
    if (published && !this._release) {
      this._release = this._provide(this.source);
    } else if (!published && this._release) {
      const release = this._release;
      this._release = null;
      release();
    }
  }
}

/** The document's dock in its editor; the editor publishes what only it can do (e.g. capture). */
export const DocumentContextDockIdentifier =
  createIdentifier<DocumentContextDock>('streamlain-document-context-dock');
